import { ChakraProvider } from "@chakra-ui/react";
import { buildLDProvider } from "@equidefi/portals/providers/LaunchDarkly";
import { initializeZxcvbn } from "@equidefi/shared";
import { EquidefiTheme } from "@equidefi/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React from "react";
import { createRoot } from "react-dom/client";

import { UnsupportedBrowserBanner } from "@equidefi/portals/components/UnsupportedBrowserBanner";
import { ApiClientProvider } from "@equidefi/portals/context/ApiClientProvider";
import AuthProvider from "@equidefi/portals/context/AuthProvider";
import App from "./app";
import { CONFIG } from "./constants/config";
import { initialize as initializeIntercom } from "./helpers/intercom";
import { sentryStart } from "./helpers/sentry";

sentryStart();

initializeIntercom();
initializeZxcvbn();

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  },
});

const container = document.getElementById("root");
const root = createRoot(container);

const renderApp = async () => {
  const LDProvider = await buildLDProvider(CONFIG.LAUNCH_DARKLY_CLIENT_ID);

  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ApiClientProvider apiUrl={CONFIG.API_URL}>
            <ChakraProvider
              toastOptions={{
                defaultOptions: {
                  isClosable: true,
                  duration: 5_000,
                  position: "top-right",
                  variant: "top-accent",
                },
              }}
              theme={EquidefiTheme}>
              <UnsupportedBrowserBanner />
              <LDProvider>
                <App />
              </LDProvider>
              <ReactQueryDevtools initialIsOpen={false} />
            </ChakraProvider>
          </ApiClientProvider>
        </AuthProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
};

renderApp();
